import React, { useState, useRef } from "react";
import { any, string, objectOf, func, shape, bool } from "prop-types";
import { debounce } from "lodash";

const propTypes = {
  keywords: string,
  handleEntry: func.isRequired,
  loading: bool,
  dictionary: objectOf(any).isRequired,
  inputOptions: shape({
    pattern: string,
    minLength: string,
    maxLength: string,
    name: string,
    id: string,
    type: string,
  }),
};

const KeywordSearch = (props) => {
  const {
    typeaheadApi,
    classNames,
    handleEntry,
    dictionary,
    keywords,
    inputOptions,
  } = props;
  const [isValid, setIsValid] = useState(true);
  const [searchValue, setSearchValue] = useState(keywords);
  const [typeaheadResults, setTypeaheadResults] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const keywordInput = useRef(null);
  const [showClearSearchInputButton, setShowClearSearchInputButton] = useState(
    Boolean(keywords)
  );

  const validateInput = () => {
    const isValid = keywordInput.current.validity.valid;
    setIsValid(isValid);
  };

  // Validate the current value of the search input field and submit if valid
  const handleSubmit = (event) => {
    event.preventDefault();
    validateInput();
    // Need to allow 0-length input to clear search to show full results
    if (
      keywordInput.current.value.length > 0 &&
      keywordInput.current.value.length < inputOptions.minLength
    ) {
      setInputMessage(dictionary.searchMinLengthMessage);
    } else if (isValid) {
      setInputMessage("");
      handleEntry(keywordInput.current.value);
    }
  };

  // Fetch typeahead suggestions based on the current value of the input field,
  // and save them in our array
  const typeahead = () => {
    if (!typeaheadApi) {
      return;
    }

    if (keywordInput.current.value.length) {
      setShowClearSearchInputButton(true);
    } else {
      setShowClearSearchInputButton(false);
    }

    if (keywordInput.current.value.length > inputOptions.minLength) {
      setInputMessage("");
    }
    validateInput();
    setTypeaheadResults([]);

    const keyword = keywordInput.current.value;

    keyword &&
      fetch(`${typeaheadApi}?q=${keyword}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          if (response.ok) {
            return response.json();
          } else {
            setTypeaheadResults([]);
          }
        })
        .then((json) => {
          // console.log("json response: ", json);
          setTypeaheadResults(json);
        });

    // console.log("typeahead results: ", typeaheadResults);
  };

  // Replace the text in the input field with the clicked typeahead
  const replaceInputText = (text) => {
    keywordInput.current.value = text;
    setTypeaheadResults([]);
    handleEntry(keywordInput.current.value);
  };

  const clearSearchField = () => {
    keywordInput.current.value = "";
    setShowClearSearchInputButton(false);
    setTypeaheadResults([]);
    keywordInput.current.focus();
    handleEntry(keywordInput.current.value);
  };

  return (
    <>
      <form
        className={`keyword-search ${classNames}`}
        onSubmit={handleSubmit}
        noValidate
        role="search"
      >
        <div className="keyword-search__input">
          <div className="keyword-search__input-typeahead">
            <input
              aria-describedby="keyword-search__message"
              className="keyword-search__input-input"
              placeholder={dictionary.searchBoxPlaceholder}
              defaultValue={searchValue || ""}
              ref={keywordInput}
              onKeyDown={debounce(typeahead, 300, {
                leading: false,
                trailing: true,
              })}
              {...inputOptions}
            />
            {showClearSearchInputButton && (
              <button
                type="button"
                className="keyword-search__clear-button"
                onClick={clearSearchField}
                aria-label="Clear search"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="9"
                  height="10"
                  fill="none"
                  viewBox="0 0 9 10"
                >
                  <path
                    fill="#000"
                    d="m6.177 5 2.517-2.368a.714.714 0 0 0 0-1.053l-.56-.527a.827.827 0 0 0-1.118 0l-2.517 2.37-2.518-2.37a.827.827 0 0 0-1.118 0l-.56.527a.714.714 0 0 0 0 1.053L2.82 5 .303 7.37a.714.714 0 0 0 0 1.053l.56.526a.825.825 0 0 0 1.118 0L4.5 6.58l2.517 2.37c.309.29.81.29 1.119 0l.559-.527a.714.714 0 0 0 0-1.053L6.177 5Z"
                  />
                </svg>
              </button>
            )}
            {typeaheadResults.length > 0 && (
              <ul className="keyword-search__typeahead">
                {/* Only show 6 typeaheads */}
                {typeaheadResults.slice(0, 6).map((typeaheadResult) => {
                  const { value } = typeaheadResult;

                  return (
                    <li key={value}>
                      <button
                        type="button"
                        className="keyword-search__typeahead-button"
                        onClick={(e) => {
                          e.preventDefault();
                          replaceInputText(value);
                        }}
                      >
                        {value}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <button type="submit" className="keyword-search__button">
            {dictionary.searchBtnText}
          </button>
        </div>
        {inputMessage && (
          <p
            role="alert"
            aria-live="polite"
            id="keyword-search__message"
            className="keyword-search__message"
          >
            {inputMessage}
          </p>
        )}
      </form>
    </>
  );
};

KeywordSearch.propTypes = propTypes;
export default KeywordSearch;
